import {clientQuery} from 'src/redux/clientQuery';
import type {ClientQueryResult} from 'src/redux/clientQuery';
import {offlineDb} from './db';
import {getReadySession} from './offlineSessionManager';
import type {OfflineSession, OfflineSnapshot} from './types';

export interface OfflineQueryOptions<Args, Result> {
  /** Session context to serve from; undefined means this call never diverts. */
  contextKey: (args: Args) => string | undefined;
  /** Cheap gate checked before any storage read (e.g. "only while offline"). */
  shouldDivert?: (args: Args) => boolean;
  /** Composes the cached result from the ready session; undefined is a miss. */
  read: (session: OfflineSession, args: Args) => Promise<Result | undefined>;
  call: (args: Args) => Promise<Result>;
}

/** Reads a hydration snapshot row by its `[sessionId+key]` composite key. */
export const readSnapshot = async (
  sessionId: string,
  key: string
): Promise<OfflineSnapshot | undefined> =>
  offlineDb.snapshots.get([sessionId, key]);

/** `readSnapshot` narrowed to its payload, typed by the caller. */
export const readSnapshotData = async <T>(
  sessionId: string,
  key: string
): Promise<T | undefined> =>
  (await readSnapshot(sessionId, key))?.data as T | undefined;

// `clientQuery`-compatible read wrapper: serves `read` from the bound ready
// session, and falls through to the network on no session, a gate, or a miss.
export const offlineQuery =
  <Args, Result>(options: OfflineQueryOptions<Args, Result>) =>
  async (args: Args): Promise<ClientQueryResult<Result>> => {
    const contextKey = options.contextKey(args);
    if (contextKey !== undefined && (options.shouldDivert?.(args) ?? true)) {
      const session = await getReadySession(contextKey);
      if (session) {
        const cached = await options.read(session, args);
        if (cached !== undefined) return {data: cached};
      }
    }
    return clientQuery(options.call)(args);
  };
